import type { ControllerPlacementCandidateTable } from "building-types/electrical/candidate";
import { InsertionWindingDirection } from "building-types/electrical/candidateTypesEnum";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { closestParamLine2 } from "geom/closestPoint";
import { line2PerpDistance } from "geom/line2";
import type { Vec2 } from "geom/vec2";
import { vec2Perp } from "geom/vec2";

const DEFAULT_SWITCH_SPACING = 3;

export function deriveControllerCandidatePlacements(
  controllerCandidateTable: ControllerPlacementCandidateTable,
  mirror: boolean,
) {
  for (const roomSetIndex in controllerCandidateTable) {
    const roomSetCandidates = controllerCandidateTable[roomSetIndex];

    for (const {
      candidate: { position, normal, insertionDirection },
      subControllerPositions,
    } of roomSetCandidates) {
      if (subControllerPositions.length < 2) {
        continue;
      }

      const [nX, nY] = normal;
      let tangentVec: Vec2;
      switch (insertionDirection) {
        case InsertionWindingDirection.FORWARD:
          tangentVec = vec2Perp(nX, nY);
          break;
        case InsertionWindingDirection.BACKWARD:
          tangentVec = vec2Perp(-nX, -nY);
          break;
        case InsertionWindingDirection.CENTER:
          // todo: this should balance switches on center, but for now, it's forward
          tangentVec = mirror ? vec2Perp(-nX, -nY) : vec2Perp(nX, nY);
          break;
      }

      const [pX, pY] = position;
      const [tX, tY] = tangentVec;

      subControllerPositions.sort((a, b) => {
        const approxA = a.loadApproximatePosition;
        assertIsNotNullOrUndefined(approxA);
        const [aX, aY] = approxA;
        const aParam = closestParamLine2(pX, pY, pX + tX, pY + tY, aX, aY, false);

        const approxB = b.loadApproximatePosition;
        assertIsNotNullOrUndefined(approxB);
        const [bX, bY] = approxB;
        const bParam = closestParamLine2(pX, pY, pX + tX, pY + tY, bX, bY, false);

        if (Math.abs(aParam - bParam) < 1) {
          const aPerpDist = line2PerpDistance(pX, pY, pX + tX, pY + tY, aX, aY);
          const bPerpDist = line2PerpDistance(pX, pY, pX + tX, pY + tY, bX, bY);
          if (Math.abs(aPerpDist - bPerpDist) < 1) {
            return aParam - bParam;
          }
          return aParam > 0 ? aPerpDist - bPerpDist : bPerpDist - aPerpDist;
        }
        return aParam - bParam;
      });

      for (const iString of Object.keys(subControllerPositions)) {
        const i = parseInt(iString);
        const pos = subControllerPositions[i];
        const scale = i * DEFAULT_SWITCH_SPACING;
        pos.subControllerPosition[0] += scale * tX;
        pos.subControllerPosition[1] += scale * tY;
      }
    }
  }
}
